import {
  Box,
  Container,
  Divider,
  Grid,
  ListSubheader,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import AddIcon from '@material-ui/icons/Add'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useDebounce, useUpdateEffect } from 'usehooks-ts'

import { Autocomplete } from '@/components/auto-complete'
import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useGetCalendarDiagnoses } from '@/hooks/queries/use-get-calendar-diagnoses'
import { useGetProjects } from '@/hooks/queries/use-get-projects'
import { useGetRegions } from '@/hooks/queries/use-get-regions'
import useAccessControl from '@/hooks/use-access-control'
import { useCompanies } from '@/hooks/use-companies'
import {
  DefaultRegisterFormData,
  useRegisterForm,
} from '@/pages/demands/steps/register/hooks/use-register-form'
import { routes } from '@/routes/config'

import { useDiagnosisRegistrationStore } from '../../contexts/diagnosis-registration-store-context'
import { useStyles } from './styles'

const imageTypesLabel = {
  raw_images: 'Imagens brutas - Sem geotags',
  raw_images_geotag: 'Imagens brutas - Com geotags',
  mosaic: 'Ortomosaicos',
}

export const defaultCalendarDiagnosis = {
  id: 'new-mapping',
  name: 'Cadastrar novo diagnóstico',
}

export const Register = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 1000)
  const { permissions } = useCompanies()
  const isWeed = permissions?.WeedAccess

  const { registerData, setRegisterData, resetDiagnosisRegistrationStore } =
    useDiagnosisRegistrationStore((state) => ({
      registerData: state.registerData,
      setRegisterData: state.setRegisterData,
      resetDiagnosisRegistrationStore: state.reset,
    }))

  const {
    control,
    register,
    handleSubmit,
    watch,
    resetField,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useRegisterForm(isWeed, {
    defaultValues: {
      calendar_diagnosis: defaultCalendarDiagnosis,
      ...registerData,
    },
  })

  const getProjectsQuery = useGetProjects()
  const projectIdValue = watch('project')?.id
  const getRegionsQuery = useGetRegions(projectIdValue, {
    enabled: !!projectIdValue && isWeed,
  })
  const getCalendarDiagnoses = useGetCalendarDiagnoses({
    search:
      debouncedSearch !== defaultCalendarDiagnosis.name ? debouncedSearch : '',
    type: 'creation',
  })

  const culture = watch('culture')
  const treatment = watch('treatment')
  const calendarDiagnosis = watch('calendar_diagnosis')

  const { accessControl, currentCulture, currentTreatment } = useAccessControl({
    treatment,
    culture,
  })

  async function onSubmit(data: DefaultRegisterFormData) {
    if (data.calendar_diagnosis.name !== search) {
      setError('calendar_diagnosis', {
        type: 'manual',
        message: 'Selecione um novo diagnóstico ou pré-cadastrado.',
      })
      return
    }

    const routeOptions = {
      planting_failure: routes.Demands('planting-failure-register'),
      weed: routes.Demands('select-files'),
      physiology: routes.Demands('physiological-register'),
    }

    resetDiagnosisRegistrationStore()
    setRegisterData(data)
    navigate(routeOptions[treatment])
  }

  const calendarDiagnoses = useMemo(
    () => [
      defaultCalendarDiagnosis,
      ...(getCalendarDiagnoses.data?.diagnoses || []),
    ],
    [getCalendarDiagnoses.data],
  )

  const projectOptions = useMemo(
    () => [
      { id: -1, name: 'Cadastrar novo projeto' },
      ...(getProjectsQuery.data || []),
    ],
    [getProjectsQuery.data],
  )

  useEffect(() => {
    resetField('region')
    if (projectIdValue === -1) {
      navigate(routes.ProjectAdd(), {
        state: { from: routes.Demands('register') },
      })
    }
  }, [projectIdValue])

  useUpdateEffect(() => {
    if (getRegionsQuery.data?.length === 1) {
      const region = getRegionsQuery.data[0]
      setValue('region', { id: region.id, name: region.name })
    }
  }, [getRegionsQuery.data])

  useUpdateEffect(() => {
    if (calendarDiagnosis?.id !== defaultCalendarDiagnosis.id) {
      reset({
        calendar_diagnosis: calendarDiagnosis,
        demand_name: calendarDiagnosis?.name,
        culture: calendarDiagnosis?.culture,
        treatment: calendarDiagnosis?.treatment,
        flight_date: calendarDiagnosis?.date,
      })
    } else {
      reset({
        calendar_diagnosis: calendarDiagnosis,
        demand_name: '',
        flight_date: '',
        culture: '' as DefaultRegisterFormData['culture'],
        treatment: '' as DefaultRegisterFormData['treatment'],
      })
    }
  }, [calendarDiagnosis?.id])

  useUpdateEffect(() => {
    if (
      calendarDiagnosis?.id !== defaultCalendarDiagnosis.id &&
      culture === calendarDiagnosis?.culture
    ) {
      resetField('treatment', {
        defaultValue: calendarDiagnosis?.treatment,
      })
    } else {
      resetField('treatment', {
        defaultValue: '' as DefaultRegisterFormData['treatment'],
      })
    }
  }, [culture, calendarDiagnosis?.id, calendarDiagnosis?.id])

  useUpdateEffect(() => {
    resetField('image_type', {
      defaultValue: '' as DefaultRegisterFormData['image_type'],
    })
  }, [treatment])

  return (
    <Container
      className={classes.main}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Box>
          <Typography variant={'h1'}>Novo Diagnóstico</Typography>
          <Typography variant={'h4'}>Informações básicas</Typography>
          <Grid container spacing={1} className={classes.container}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='calendar_diagnosis'
                defaultValue={defaultCalendarDiagnosis}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id='calendar_diagnosis'
                    classes={{
                      listbox: classes.menuList,
                      inputRoot: classes.backgroundGrayInput,
                    }}
                    options={calendarDiagnoses}
                    onChange={(_, newValue: any) => onChange(newValue)}
                    value={value}
                    freeSolo
                    loading={getCalendarDiagnoses.isFetching}
                    groupBy={(option) =>
                      option.id !== defaultCalendarDiagnosis.id
                        ? 'diagnosis'
                        : ''
                    }
                    renderGroup={(params) => (
                      <Fragment key={params.key}>
                        {params.group === 'diagnosis' && (
                          <ListSubheader style={{ backgroundColor: 'white' }}>
                            Selecione um dos diagnósticos abaixo
                          </ListSubheader>
                        )}
                        {params.children}
                      </Fragment>
                    )}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(option) =>
                      option.id === defaultCalendarDiagnosis.id ? (
                        <Box display='flex' gridGap='1rem'>
                          <Add />
                          <Typography variant='subtitle1'>
                            {option.name}
                          </Typography>
                        </Box>
                      ) : (
                        option.name
                      )
                    }
                    filterOptions={(options, params) =>
                      options.filter((option) => {
                        if (!params.inputValue) return true

                        const searchValue = params.inputValue.toLowerCase()

                        return option.name.toLowerCase().includes(searchValue)
                      })
                    }
                    optionsError={
                      !getCalendarDiagnoses.isFetching &&
                      getCalendarDiagnoses.isError
                    }
                    noOptionsText='Não foi encontrado mapeamentos para a companhia atual.'
                    inputTextProps={{
                      label: 'Selecione um diagnóstico pré-cadastrado',
                      error:
                        (!getCalendarDiagnoses.isFetching &&
                          getCalendarDiagnoses.isError) ||
                        !!error,
                      helperText:
                        !getCalendarDiagnoses.isFetching &&
                        getCalendarDiagnoses.isError
                          ? 'Não foi encontrado mapeamentos para a companhia atual.'
                          : error?.message,
                    }}
                    onInputChange={(_, newInputValue) => {
                      setSearch(newInputValue)
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='demand_name'
                defaultValue=''
                render={({ field, fieldState: { error } }) => (
                  <InputText
                    id='demand_name'
                    label={'Nome do diagnóstico'}
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>{' '}
            <Grid item xs={12}>
              <Controller
                control={control}
                name='project'
                defaultValue={null as any}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id='project'
                    options={projectOptions}
                    onChange={(_, newValue: any) =>
                      onChange(
                        newValue && { id: newValue.id, name: newValue.name },
                      )
                    }
                    freeSolo
                    value={value}
                    loading={getProjectsQuery.isFetching}
                    getOptionLabel={(option) => option.name}
                    renderOption={(option) =>
                      option.id < 0 ? (
                        <Box display='flex' gridGap='1rem'>
                          <AddIcon />
                          <Typography variant='subtitle1'>
                            {option.name}
                          </Typography>
                        </Box>
                      ) : (
                        option.name
                      )
                    }
                    optionsError={
                      !getProjectsQuery.isFetching &&
                      getProjectsQuery.isError &&
                      getProjectsQuery.errorUpdateCount < 3
                    }
                    onRetry={() => getProjectsQuery.refetch()}
                    noOptionsText='Não foi encontrado projetos para a companhia atual.'
                    inputTextProps={{
                      label: 'Selecione o projeto',
                      error:
                        (!getProjectsQuery.isFetching &&
                          getProjectsQuery.isError) ||
                        !!error,
                      helperText:
                        !getProjectsQuery.isFetching && getProjectsQuery.isError
                          ? 'Não foi encontrado projetos para a companhia atual.'
                          : error?.message,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {isWeed && (
                <Controller
                  control={control}
                  name='region'
                  defaultValue={null as any}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Autocomplete
                      id='region'
                      options={getRegionsQuery.data || []}
                      onChange={(_, newValue: any) =>
                        onChange(
                          newValue && { id: newValue.id, name: newValue.name },
                        )
                      }
                      freeSolo
                      value={value}
                      loading={getRegionsQuery.isFetching}
                      getOptionLabel={(option) => option.name}
                      optionsError={
                        !getRegionsQuery.isFetching &&
                        getRegionsQuery.isError &&
                        getRegionsQuery.errorUpdateCount < 3
                      }
                      onRetry={() => getRegionsQuery.refetch()}
                      noOptionsText='Não foi encontrado regiões para o projeto selecionado.'
                      inputTextProps={{
                        label: 'Selecionar região',
                        error:
                          (!getRegionsQuery.isFetching &&
                            getRegionsQuery.isError) ||
                          !!error,
                        helperText:
                          !getRegionsQuery.isFetching && getRegionsQuery.isError
                            ? 'Não foi encontrado regiões para o projeto selecionado.'
                            : error?.message,
                      }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='culture'
                control={control}
                defaultValue={'' as any}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputText
                    select
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                        classes: { list: classes.menuList },
                      },
                    }}
                    id='culture'
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value)
                    }}
                    label={'Selecione a cultura'}
                    error={!!error}
                    helperText={error?.message}
                  >
                    <ListSubheader value=''>Selecione a cultura</ListSubheader>

                    {Object.entries(accessControl).map(
                      ([culture_key, culture]) =>
                        culture?.enabled && (
                          <MenuItem value={culture_key} key={culture_key}>
                            {culture?.label}
                          </MenuItem>
                        ),
                    )}
                  </InputText>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='treatment'
                control={control}
                defaultValue={'' as any}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputText
                    select
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                        classes: { list: classes.menuList },
                      },
                    }}
                    id='treatment'
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value)
                    }}
                    label={'Selecione o diagnóstico'}
                    error={!!error}
                    helperText={error?.message}
                  >
                    <ListSubheader value=''>Diagnóstico</ListSubheader>
                    {currentCulture &&
                      Object.entries(currentCulture.treatments).map(
                        ([treatment_key, treatment]) =>
                          treatment?.enabled && (
                            <MenuItem value={treatment_key} key={treatment_key}>
                              {treatment?.label}
                            </MenuItem>
                          ),
                      )}
                  </InputText>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name='image_type'
                defaultValue={'' as any}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputText
                    select
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                        classes: { list: classes.menuList },
                      },
                      renderValue: (value) =>
                        imageTypesLabel[value as keyof typeof imageTypesLabel],
                    }}
                    id='image_type'
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    label={'Selecione o tipo de imagem'}
                    error={!!error}
                    helperText={error?.message}
                  >
                    {currentTreatment?.imagesTypes.raw_images && (
                      <ListSubheader aria-label='Grupo imagens brutas' value=''>
                        Imagens brutas
                      </ListSubheader>
                    )}

                    {currentTreatment?.imagesTypes.raw_images &&
                      currentTreatment.imagesTypes.raw_images.map(
                        (imageType) => (
                          <MenuItem
                            value={imageType.value}
                            key={imageType.value}
                          >
                            {imageType.label}
                          </MenuItem>
                        ),
                      )}

                    {currentTreatment?.imagesTypes?.mosaic && (
                      <ListSubheader aria-label='Grupo ortomosaicos' value=''>
                        Ortomosaicos
                      </ListSubheader>
                    )}

                    {currentTreatment?.imagesTypes?.mosaic &&
                      currentTreatment.imagesTypes.mosaic.map((imageType) => (
                        <MenuItem value={imageType.value} key={imageType.value}>
                          {imageType.label}
                        </MenuItem>
                      ))}
                  </InputText>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                id='flight_date'
                type={'date'}
                label={'Data do Voo'}
                {...register('flight_date')}
                InputLabelProps={{ shrink: true }}
                error={!!errors.flight_date}
                helperText={errors.flight_date?.message}
                onKeyDown={(e) => e.preventDefault()}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Divider />
      <Box className={classes.buttonContainer}>
        <Link to='/diagnoses'>
          <Button outlined>Cancelar</Button>
        </Link>
        <Button type='submit'>Avançar</Button>
      </Box>
    </Container>
  )
}
